import React, { createContext, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { resourceRoutes } from '@/resources/resource-map';

interface RouteParams {
	[key: string]: string;
}

interface RouteContextValue {
	params: RouteParams;
}

const RouteContext = createContext<RouteContextValue>({ params: {} });

interface RouteProviderProps {
	children: ReactNode;
}

export const RouteProvider: React.FC<RouteProviderProps> = ({ children }) => {
	const location = useLocation();

	// Parse parameters from current path
	const parseParams = (): RouteParams => {
		// Find matching route
		const matchingRoute = resourceRoutes.find(route => {
			const routeParts = route.path.split('/').filter(Boolean);
			const pathParts = location.pathname.split('/').filter(Boolean);

			if (routeParts.length !== pathParts.length) {
				return false;
			}

			return routeParts.every((part, index) => {
				return part.startsWith(':') || part === pathParts[index];
			});
		});

		if (!matchingRoute) return {};

		// Extract parameters
		const params: RouteParams = {};
		const routeParts = matchingRoute.path.split('/').filter(Boolean);
		const pathParts = location.pathname.split('/').filter(Boolean);

		routeParts.forEach((part, index) => {
			if (part.startsWith(':')) {
				params[part.slice(1)] = pathParts[index];
			}
		});

		return params;
	};

	const params = parseParams();

	return (
		<RouteContext.Provider value={{ params }}>
			{children}
		</RouteContext.Provider>
	);
};

// Custom hook to replace useParams
export const useRouteParams = (): RouteParams => {
	const context = useContext(RouteContext);
	return context.params;
};
